import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Link from '@amzn/meridian/link';
import SideMenu, {SideMenuLink, SideMenuTitle} from '@amzn/meridian/side-menu';
import Text from '@amzn/meridian/text';
import Theme from '@amzn/meridian/theme';
import Tooltip from '@amzn/meridian/tooltip';
import {colorGray100} from '@amzn/meridian-tokens/base/color';
import {
  findCurrentShop,
  getStoreDetails,
} from '@amzn/rangoli-seller-desktop-fe-auth-utils/src/index';
import {
  commonUnityStore,
  FeatureFlags,
  getFeatureFlagStatus,
  getMicroFrontendPath,
  getShopId,
  MICRO_FRONTENDS_CONFIG,
  MicroFrontends,
  MicroFrontendSubModules,
} from '@amzn/rangoli-seller-desktop-utils/src/index';
import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import googlePlayButton from '../../assets/googlePlayButton.svg';
import googlePlayQR from '../../assets/googlePlayQR.svg';
import smartBizLogo from '../../assets/logo.svg';
import QRScanner from '../../components/QRScanner/QRScanner';
import {redirectionUrls} from '../../constants/apiConstants';
import {ImageAlternatives} from '../../constants/mainHeaderConstant';
import {ModulePaths, parentPaths, SidebarContent} from '../../constants/SideBarConstants';
import {videoCommerceAllowedOnboardingStatus} from '../../constants/videoCommerceConstants';
import {helpAndSupportStore} from '../../store/HelpAndSupportStore';
import {sidebarStore} from '../../store/SidebarStore';
import {borderRadiusStyle} from '../../Style/Style';
import SidebarChildMenuLink from './SidebarChildMenuLink';
import SidebarMenuLink from './SidebarMenuLink';
import homeIcon from "../../assets/Icons/HomeIcon.svg"
import catalogIcon from "../../assets/Icons/CatalogIcon.svg"
import ordersIcon from "../../assets/Icons/OrdersIcon.svg"
import analyticsIcon from "../../assets/Icons/AnalyticsIcon.svg"
import reviewsIcon from "../../assets/Icons/ReviewsIcon.svg"
import storeIcon from "../../assets/Icons/StoreIcon.svg"
import growthIcon from "../../assets/Icons/GrowthIcon.svg"
import trustMarkersIcon from "../../assets/Icons/TrustMarkerIcon.svg"
import settingsIcon from "../../assets/Icons/SettingsIcon.svg"
import connectionsIcon from "../../assets/Icons/ConnectionsIcon.svg"
import {ShopOwnership} from '../../constants/ShopOwnership';

const {SMARTBIZ, GOOGLE_PLAY} = ImageAlternatives;

export interface ISidebarProps {
  showMobileView: boolean;
}

const Sidebar = ({showMobileView}: ISidebarProps): React.JSX.Element => {
  const [selectedGroupedSidebar, setSelectedGroupedSidebar] = useState<string[]>([]);
  const [isVideoCommerceEnabled, setIsVideoCommerceEnabled] = useState<boolean>(null);
  const [storeDetails, setStoreDetails] = useState<any>(null);
  const [isPrimarySeller, setIsPrimarySeller] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  const {handleChangeActiveProfileDetails} = helpAndSupportStore;
  const { ANALYTICS_ENABLED_STATUS} = FeatureFlags;

  const updateRoutes = () => {
    const pathSegments = location.pathname.split('/');
    // The trailing slash must be removed for other routes, but in the case of the orders, the slash indicates the all orders page, so we need to include a check here.
    if (
      pathSegments[pathSegments.length - 1] === '' &&
      pathSegments[pathSegments.length - 2] !== SidebarContent.orders
    ) {
      pathSegments.pop();
    }
    if (pathSegments.length === 2 && parentPaths.includes(pathSegments[1])) {
      sidebarStore.setParentRoute(pathSegments[1]);
      sidebarStore.setChildRoute('');
    } else {
      let childPath =
        pathSegments
          .slice(1)
          .reverse()
          .find((segment) => ModulePaths.some((pathArray) => pathArray.includes(segment))) || '';
      if (!Number.isNaN(Number(childPath))) childPath = "";
      sidebarStore.setChildRoute(childPath);
      const parentPath = ModulePaths.find((path) => path.includes(childPath))?.[0]?.toLowerCase();
      if (parentPath) sidebarStore.setParentRoute(parentPath);
      const isGroupedSidebarSelected = selectedGroupedSidebar.includes(parentPath);
      if (!isGroupedSidebarSelected) {
        setSelectedGroupedSidebar(prev => [...prev, parentPath]);
      }
    }
  }
  const handleSideMenuSelection = (): void => {
    // Verifying if the parent route is selected
    const currentFeature = Object.values(MICRO_FRONTENDS_CONFIG).find(
      (item) =>
        sidebarStore.childRoute === item.path ||
        (sidebarStore.childRoute?.length > 0 && item.path.endsWith(sidebarStore.childRoute))
    );
    const isVideoCommerceRoute =
      currentFeature?.path === MICRO_FRONTENDS_CONFIG[MicroFrontends.VIDEO_COMMERCE].path;
    const isGoogleAnalyticsRoute =
      currentFeature?.path === MICRO_FRONTENDS_CONFIG[MicroFrontends.GOOGLE_ANALYTICS].path;
    const isUAMRoute =
      currentFeature?.path === MICRO_FRONTENDS_CONFIG[MicroFrontends.USER_MANAGEMENT].path;

    const shouldNavigateToHome =
      (sidebarStore.parentRoute === '' && !currentFeature) ||
      (isVideoCommerceRoute && !isPrimarySeller && !isVideoCommerceEnabled) ||
      (isGoogleAnalyticsRoute && !isPrimarySeller) ||
      (isUAMRoute && !sidebarStore.isUserManagementEnabled);
    if (shouldNavigateToHome) {
      console.warn('Feature not available, navigating to home...');
      navigate(MICRO_FRONTENDS_CONFIG[MicroFrontends.HOME].path);
    }
    const isGroupedSidebarSelected = selectedGroupedSidebar.includes(sidebarStore.parentRoute);
    if (sidebarStore.parentRoute.length > 0 && sidebarStore.childRoute.length > 0) {
      if (!isGroupedSidebarSelected) {
        setSelectedGroupedSidebar(prev => [...prev, sidebarStore.parentRoute]);
      }
    }
  };

  const loadStoreDetails = async (): Promise<any> => {
    const storeDetails = await getStoreDetails();
    if (storeDetails?.successCode === 'SUCCESS') {
      const currentStore = findCurrentShop(storeDetails?.data);
      localStorage.setItem('storeURI', currentStore?.storeURI);
      setStoreDetails(storeDetails);
      if (currentStore?.shopOwnership === ShopOwnership.PRIMARY) {
        setIsPrimarySeller(true);
      } else if (currentStore?.shopOwnership === ShopOwnership.SECONDARY) {
        setIsPrimarySeller(false);
      }
    }
  };

  const handleVideoCommerceDetails = async (): Promise<void> => {
    // Fetching the onboarding status for video commerce
    const currentStore = findCurrentShop(storeDetails?.data);
    const videoCommerceOnboardingStatus =
      currentStore?.storeConfig?.videoCommerceOnboardingStatus;
    const isVideCommerceOnboarded: boolean = videoCommerceAllowedOnboardingStatus?.includes(
      videoCommerceOnboardingStatus
    );
    setIsVideoCommerceEnabled(isVideCommerceOnboarded);
  };

  useEffect((): void => {
    loadStoreDetails();
    fetchAnalyticsEnabled();
  }, []);

  useEffect((): void => {
    handleVideoCommerceDetails();
  }, [storeDetails]);

  useEffect(() => {
    updateRoutes();
  }, [location.pathname]);

  useEffect((): void => {
    if (isVideoCommerceEnabled !== null || isPrimarySeller !== null) {
      handleSideMenuSelection();
    }
  }, [isVideoCommerceEnabled, isPrimarySeller]);

  const handleOnClickNavigation = (): void => {
    const navigatePath: string = getMicroFrontendPath(MicroFrontends.HOME);
    navigate(navigatePath);
    handleChangeActiveProfileDetails(false);
    sidebarStore.setSidebarToggle();
  };

  const isShowSideBar = (): boolean => (showMobileView ? sidebarStore.sidebarToggle : true);

  const handleGooglePlayNavigation = (): void => {
    if (showMobileView) {
      window.location.replace(redirectionUrls.sellerAppGooglePlayStore);
    } else {
      sidebarStore.handleScannerToggle();
    }
  };

  const toggleSelectedGroupedSidebar = (sideBarItem: string): void => {
    setSelectedGroupedSidebar((prevIncludedItems) => {
      if (prevIncludedItems.includes(sideBarItem)) {
        return prevIncludedItems.filter((item) => item !== sideBarItem);
      } else {
        return [...prevIncludedItems, sideBarItem];
      }
    });
  };

  const GooglePlayImage = (): React.JSX.Element => {
    return (
      <Column spacingInset="300" backgroundColor={colorGray100} className={borderRadiusStyle}>
        <Text type="b200" color="secondary">
          {SidebarContent.navigateToMobileApp}
        </Text>
        <Link onClick={() => handleGooglePlayNavigation()}>
          <img src={googlePlayButton} height="47px" width="160px" alt={GOOGLE_PLAY}/>
        </Link>
      </Column>
    );
  };

  const fetchAnalyticsEnabled = async (): Promise<void> => {
    const shopId = await getShopId();
    const isAnalyticsFeatureEnabled = await getFeatureFlagStatus(
      ANALYTICS_ENABLED_STATUS.flagName,
      ANALYTICS_ENABLED_STATUS.defaultValue,
      shopId
    );
    sidebarStore.setIsAnalyticsFeatureEnabled(isAnalyticsFeatureEnabled);
  };

  const SidebarDivider = (): React.JSX.Element => (
    <Column width="100%" spacingInset="400 450">
      <Divider/>
    </Column>
  );

  //adding window event for communication across MFEs. window object should only be used for creating/listening to custom events and not otherwise.
  useEffect((): void => {
    window.addEventListener('HIDE_SIDE_BAR', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      sidebarStore.setShowSideBar(!detail.hideSideBar);
    });
  });

  return (
    <Theme
      tokens={{
        dividerColor: '#E7E9E9',
      }}
    >
      {sidebarStore.showSideBar && (
        <>
          <SideMenu
            width="260px"
            type={showMobileView ? 'overlay' : 'skinny'}
            open={isShowSideBar()}
            {...(showMobileView && {onClose: sidebarStore.setSidebarToggle})}
            linkComponents={[SidebarMenuLink, SidebarChildMenuLink, SideMenuLink, SidebarDivider]}
          >
            {showMobileView && (
              <SideMenuTitle onClick={handleOnClickNavigation}>
                <img src={smartBizLogo} alt={SMARTBIZ}/>
              </SideMenuTitle>
            )}
            <SidebarMenuLink MicroFrontendsKey={MicroFrontends.HOME} icon={homeIcon}/>
            <SidebarMenuLink
              MicroFrontendsKey={MicroFrontends.CATALOG}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              icon={catalogIcon}
            >
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.CATALOG}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.CATALOG].PRODUCTS}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.CATALOG}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.CATALOG].CATEGORIES}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.CATALOG}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.CATALOG].COLLECTIONS}
              />
            </SidebarMenuLink>
            <SidebarMenuLink
              MicroFrontendsKey={MicroFrontends.OMS_MODULE}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              icon={ordersIcon}
            >
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.OMS_MODULE}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.OMS_MODULE].ALL_ORDERS}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.OMS_MODULE}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.OMS_MODULE].RETURNS}
                isNewFeature={true}
              />
            </SidebarMenuLink>
            <SidebarMenuLink
              MicroFrontendsKey={MicroFrontends.ANALYTICS}
              icon={analyticsIcon}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
            >
              {sidebarStore.isAnalyticsFeatureEnabled && <SidebarChildMenuLink
              parentMicrofrontendKey={MicroFrontends.ANALYTICS}
              subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].SALES_AND_ORDERS}
              />}
              {sidebarStore.isAnalyticsFeatureEnabled && <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].TRAFFIC}
              />}
              {sidebarStore.isAnalyticsFeatureEnabled &&<SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].OPERATIONS}
              />}
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].ORDERS}
              />
                <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].OFFERS}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].SALES}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].RETURNS}
              />
              <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].CUSTOMER_DETAILS}
              />
              {sidebarStore.isCustomerSegmentsEnabled && (
                <SidebarChildMenuLink
                  parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                  subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].CUSTOMER_SEGMENTS}
                />
              )}
              {sidebarStore.isAnalyticsFeatureEnabled && <SidebarChildMenuLink
                parentMicrofrontendKey={MicroFrontends.ANALYTICS}
                subModuleKey={MicroFrontendSubModules[MicroFrontends.ANALYTICS].REPORTS}
              />}
            </SidebarMenuLink>
            {sidebarStore.isReviewsEnabled && (
              <SidebarMenuLink
                MicroFrontendsKey={MicroFrontends.REVIEWS}
                icon={reviewsIcon}
                selectedGroupedSidebar={selectedGroupedSidebar}
                toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              >
                <SidebarChildMenuLink
                  parentMicrofrontendKey={MicroFrontends.REVIEWS}
                  subModuleKey={MicroFrontendSubModules[MicroFrontends.REVIEWS].MANAGE}
                />
              </SidebarMenuLink>
            )}
            <SidebarDivider/>

            <SidebarMenuLink
              title={SidebarContent.storeConfiguration}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              icon={storeIcon}
            >
              <SidebarChildMenuLink
                subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.WEBSITE_APPEARANCE].name}
                subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.WEBSITE_APPEARANCE].path}
              />
              {/* Hide Google Analytics option for secondary sellers */}
              {isPrimarySeller && (
                <SidebarChildMenuLink
                  subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.GOOGLE_ANALYTICS].name}
                  subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.GOOGLE_ANALYTICS].path}
                  isNewFeature={true}
                />
              )}
            </SidebarMenuLink>
            <SidebarMenuLink
              title={SidebarContent.growth}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              icon={growthIcon}
            >
              <SidebarChildMenuLink
                subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.OFFERS].name}
                subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.OFFERS].path}
              />
              <SidebarChildMenuLink
                subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.PERFORMANCE_MARKETING].name}
                subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.PERFORMANCE_MARKETING].path}
              />
              {commonUnityStore.isSEOFeatureEnabled && (
                <SidebarChildMenuLink
                  subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.SEO].name}
                  subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.SEO].path}
                />
              )}
              {/* Hide Video Commerce option for secondary sellers */}
              {isVideoCommerceEnabled && isPrimarySeller && (
                <SidebarChildMenuLink
                  subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.VIDEO_COMMERCE].name}
                  subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.VIDEO_COMMERCE].path}
                />
              )}
              {/* Hide Marketing Automation option for secondary sellers */}
              {commonUnityStore.isMAFeatureEnabled && isPrimarySeller && (
                <SidebarChildMenuLink
                  subModuleKey={MICRO_FRONTENDS_CONFIG[MicroFrontends.MARKETING_AUTOMATION].name}
                  subModulePath={MICRO_FRONTENDS_CONFIG[MicroFrontends.MARKETING_AUTOMATION].path}
                />
              )}
              <SidebarChildMenuLink
                subModuleKey={SidebarContent.instagramFeedText}
                subModulePath={SidebarContent.instagramFeedPath}
                isNotAvailableInDesktop={true}
              />
              <SidebarChildMenuLink
                subModuleKey={SidebarContent.sellUsingWhatsappText}
                subModulePath={SidebarContent.sellUsingWhatsappPath}
                isNotAvailableInDesktop={true}
              />
            </SidebarMenuLink>
            <SidebarMenuLink
              title={SidebarContent.trustMarkers}
              selectedGroupedSidebar={selectedGroupedSidebar}
              toggleSelectedGroupedSidebar={toggleSelectedGroupedSidebar}
              icon={trustMarkersIcon}
            >
              <SidebarChildMenuLink
                subModuleKey={SidebarContent.trustBadgesText}
                subModulePath={SidebarContent.trustBadgesPath}
                isNotAvailableInDesktop={true}
              />
            </SidebarMenuLink>
            <SidebarMenuLink MicroFrontendsKey={MicroFrontends.SETTINGS} icon={settingsIcon}/>
            {showMobileView ? (
              <GooglePlayImage/>
            ) : (
              <Tooltip
                position="top"
                title={
                  <Column alignmentHorizontal="center" alignmentVertical="center" width="152px">
                    <img src={googlePlayQR} alt={GOOGLE_PLAY} width="140px" height="140px"/>
                    <Text type="b100" alignment="center" color="inverted">
                      {SidebarContent.scannerTootTipContent}
                    </Text>
                  </Column>
                }
                id="myTooltip"
              >
                <GooglePlayImage/>
              </Tooltip>
            )}
            <QRScanner/>
          </SideMenu>
        </>
      )}
    </Theme>
  );
};

export default observer(Sidebar);
